/**
 * Shared furniture for the three audit and analytics surfaces — `<page-audit>`,
 * `<page-audit-record>` and `<page-analytics>`.
 *
 * It exists because `04` §6.1 requires **three things that are not charts** on
 * every one of those screens — the scope, the ingest watermark and the profile
 * badge — and `04` §4.4 requires the chain state on all of them too. Four
 * obligations × three pages is where copy drifts, and drifting copy on an honesty
 * requirement is the same bug as not having it: if the explorer says a gap and the
 * dashboard says nothing, the reader believes the dashboard.
 *
 * It carries no element definitions. Pages compose [auditSharedCss] into their own
 * `static styles` and call the template helpers, so everything still lives in one
 * shadow root per page and the kit stays the only source of components.
 */

import { css, html, nothing, type TemplateResult } from "lit";

import type { ApiError } from "../api/errors.js";
import {
  LAG_WARN_SECONDS,
  type AuditProfileName,
  type AuditProjection,
  type ChainState,
  type IngestState,
  type IngestStatus,
} from "../api/audit.js";
import type { AnalyticsSource, AnalyticsWatermark } from "../api/analytics.js";
import type { BnBadgeVariant, BnSelectOption } from "../components/index.js";
import { getPolicyFormSchema } from "../api/policies.js";
import { hrefFor } from "../nav.js";

/* ── formatting ─────────────────────────────────────────────────────────── */

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date" if the server ever sends something this bundle
 * cannot parse. */
export function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * whenUtc renders a timestamp in UTC, explicitly labelled.
 *
 * Every day boundary in this product is UTC (`04` §6.1) — a per-organization
 * timezone would make our buckets disagree with the gateway's own budget counters,
 * which are authoritative per UTC day — so the watermark and the axis are stated
 * in UTC and say so.
 */
export function whenUtc(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

/**
 * rangeLabel is a compact UTC window for a stat tile or a card head — the string
 * that satisfies "every tile states its window" without spending three lines on it.
 *
 * It is UTC and says so once, at the end, rather than twice. The long form
 * ([whenUtc] on each bound) stays where the exact instant matters: the range echo
 * under the explorer's table, and the export dialog.
 */
export function rangeLabel(from: string | undefined, to: string | undefined): string {
  if (!from || !to) return "";
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "";
  const fmt = new Intl.DateTimeFormat(undefined, {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${fmt.format(a)} → ${fmt.format(b)} UTC`;
}

/** ago renders a duration in seconds as "2 minutes ago", or "just now" under a
 * minute. It never renders "live": the sink batches, so there is always a floor
 * and claiming otherwise is the misreading `SC-S806` exists to prevent. */
export function ago(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} s ago`;
  const fmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (seconds < 3600) return fmt.format(-Math.round(seconds / 60), "minute");
  if (seconds < 86400) return fmt.format(-Math.round(seconds / 3600), "hour");
  return fmt.format(-Math.round(seconds / 86400), "day");
}

/** duration renders a millisecond count for a table cell. */
export function duration(ms: number | undefined): string {
  if (ms === undefined || ms === null) return "—";
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/** count renders an integer with locale grouping. */
export function count(n: number | undefined): string {
  return n === undefined || n === null ? "—" : n.toLocaleString();
}

/** compact renders a headline number: `1,284` / `12.9K` / `4.2M`. Stat-tile and
 * hero values use it so a five-figure count does not blow the tile's width. */
export function compact(n: number): string {
  if (Math.abs(n) < 10_000) return n.toLocaleString();
  return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

/** bytes renders a byte count for the export result. */
export function bytes(n: number | undefined): string {
  if (n === undefined) return "—";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${i === 0 ? v : v.toFixed(1)} ${units[i]}`;
}

/** shortHash truncates a 64-hex digest for a cell. The **full** value is always
 * reachable — `bn-kv` gives every row a copy button — because `SC-S805`'s second
 * criterion requires exactly that. */
export function shortHash(hash: string | undefined): string {
  if (!hash) return "—";
  return hash.length <= 20 ? hash : `${hash.slice(0, 12)}…${hash.slice(-6)}`;
}

/* ── the three frame elements of `04` §6.1 ─────────────────────────────── */

/**
 * WatermarkView is the common shape behind the two watermark blocks on the wire.
 *
 * `internal/queryaudit` and `internal/analytics` answer different structures for
 * the same fact — the audit one carries the cursor state and the holdings, the
 * analytics one carries whether the requested window is fully covered — so the
 * pages normalise into this and there is one piece of copy for both.
 */
export interface WatermarkView {
  asOf: string | null;
  /** Null means nothing has been ingested at all, which is a third state and not
   * zero lag. */
  lagSeconds: number | null;
  stale: boolean;
  /** False when the requested window extends past `asOf`. Undefined on the audit
   * block, which does not compute it. */
  complete?: boolean;
  state?: IngestState;
  lastError?: string;
  objects?: number;
  records?: number;
}

/** fromIngestStatus adapts an audit response's `ingest`. */
export function fromIngestStatus(s: IngestStatus): WatermarkView {
  return {
    asOf: s.as_of,
    lagSeconds: s.lag_seconds,
    stale: s.stale,
    state: s.state,
    lastError: s.last_error,
    objects: s.objects,
    records: s.records,
  };
}

/** fromAnalyticsWatermark adapts an analytics response's `ingest`. */
export function fromAnalyticsWatermark(w: AnalyticsWatermark): WatermarkView {
  return {
    asOf: w.as_of,
    lagSeconds: w.as_of === null ? null : w.lag_seconds,
    stale: w.stale,
    complete: w.complete,
  };
}

/**
 * watermarkLine renders the ingest watermark (interface I8, `SC-S806`).
 *
 * This is not a nicety. Without it a user reading a screen at 14:33 sees a cliff
 * at the right edge and concludes traffic stopped, when what they are seeing is
 * the sink's upload interval — the single most likely misreading of the whole
 * product. Three states, all distinct on screen:
 *
 *   - **nothing ingested** — no records have arrived for this instance yet, so an
 *     empty result means "not ingested", not "no queries". Said in words.
 *   - **behind the alert threshold** — a warning that names the current lag,
 *     because the same 15 minutes is what the operational alert fires on.
 *   - **caught up** — the actual lag value, never the word "live".
 */
export function watermarkLine(w: WatermarkView): TemplateResult {
  if (w.asOf === null || w.lagSeconds === null) {
    return html`<p class="notice notice-warn" role="status">
      <strong>No audit records have been ingested for this instance yet.</strong> An empty result
      here means “not ingested”, not “no queries were handled”. Ingest state:
      <code>${w.state ?? "unknown"}</code>.${w.lastError
        ? html` Last ingest error: <code>${w.lastError}</code>.`
        : nothing}
    </p>`;
  }
  const stale = w.stale || w.lagSeconds > LAG_WARN_SECONDS;
  return html`<p class=${`notice ${stale ? "notice-warn" : "notice-quiet"}`} role="status">
    Data current to <strong>${whenUtc(w.asOf)}</strong> (${ago(w.lagSeconds)}).
    ${stale
      ? html`<strong>Data is delayed.</strong> The ingest watermark is more than
          ${Math.round(LAG_WARN_SECONDS / 60)} minutes old, which is the same threshold the
          operational alert fires on. Records after the watermark may exist and have not arrived.`
      : html`The audit sink batches and uploads on size or interval, so there is always a floor —
          this is the real lag, not a rounding of “live”.`}
    ${w.complete === false
      ? html` The requested window extends past the watermark, so its trailing region is
          <strong>incomplete</strong> rather than zero.`
      : nothing}
    ${w.state === "stalled"
      ? html` Ingest is <strong>stalled</strong>.${w.lastError
          ? html` Last error: <code>${w.lastError}</code>.`
          : nothing}`
      : nothing}
    ${w.state === "chain_broken"
      ? html` The ingest cursor reports <strong>chain_broken</strong>: what was ingested does not
          link. This is distinct from stalled and is investigated from the chain panel.`
      : nothing}
  </p>`;
}

/** PROFILE_META is `SC-F821`'s vocabulary with the sentence each value obliges.
 * The copy is here rather than inline so the explorer, the record view and the
 * dashboard cannot describe the same instance differently. */
export const PROFILE_META: Record<
  AuditProfileName,
  { label: string; variant: BnBadgeVariant; note: string }
> = {
  standard: {
    label: "Standard",
    variant: "neutral",
    note: "This instance captures a SQL sample of up to 2048 bytes per record, and Sluice Cloud stores it.",
  },
  minimized: {
    label: "Minimized",
    variant: "info",
    note:
      "This instance renders audit.sqlSampleBytes: 0, so no SQL text is captured at the source — " +
      "not captured and then dropped by us. Views whose content depends on query text are " +
      "unavailable by your organization's own choice, and every other filter and chart still works.",
  },
  customer_held: {
    label: "Customer-held",
    variant: "warning",
    note:
      "This instance writes its audit to your own bucket with your own credentials, so Sluice Cloud " +
      "stores no records at all. The chain attestation below is real; the record list is empty by design.",
  },
};

/** profileBadge renders the profile pill `SC-F821` requires on **every** audit and
 * analytics surface. */
export function profileBadge(profile: AuditProfileName | "" | undefined): TemplateResult | typeof nothing {
  if (!profile) return nothing;
  const meta = PROFILE_META[profile];
  if (!meta) return nothing;
  return html`<bn-badge .variant=${meta.variant} title=${meta.note}>
    Audit profile: ${meta.label}
  </bn-badge>`;
}

/**
 * CHAIN_STATE_META is `04` §4.4's three outcomes, and the reason each is a
 * separate row here rather than a boolean is stated in the table: a verifier with
 * only ok/broken would page an operator every time an instance was hard-killed,
 * and **a false BROKEN is worse than no verification at all**.
 */
export const CHAIN_STATE_META: Record<
  ChainState,
  { label: string; variant: BnBadgeVariant; note: string }
> = {
  VERIFIED: {
    label: "VERIFIED",
    variant: "success",
    note:
      "Contiguous prior_hash linkage across this range, every recomputed hash matches, and the " +
      "first record matches the registered genesis anchor.",
  },
  GAP: {
    label: "GAP",
    variant: "warning",
    note:
      "Every recomputed hash matches, but records are missing between two intact stretches. " +
      "Sluice Cloud holds a best-effort copy: an instance killed mid-batch or an unreachable object " +
      "store produces exactly this. It is reconcilable and it is NOT tampering.",
  },
  BROKEN: {
    label: "BROKEN",
    variant: "error",
    note:
      "The bytes held here are not what the writer wrote: a recomputed hash does not match, a " +
      "segment's first record does not match the registered anchor, or a line is unparseable. " +
      "This is the one alarm in the product that means the integrity property has failed.",
  },
};

/** chainBadge renders one chain state, never collapsed to healthy/unhealthy. */
export function chainBadge(state: ChainState | undefined): TemplateResult | typeof nothing {
  if (!state) return nothing;
  const meta = CHAIN_STATE_META[state] ?? {
    label: state,
    variant: "warning" as BnBadgeVariant,
    note: "An outcome this build does not recognize. Treated as the worst value, failing safe.",
  };
  return html`<bn-badge .variant=${meta.variant} title=${meta.note}>${meta.label}</bn-badge>`;
}

/**
 * projectionNotice renders `06` §3.12.1's projection block whenever anything was
 * withheld.
 *
 * The server **omits** the sensitive keys rather than blanking them, and the
 * client half of that contract is this notice: a record shown without its SQL and
 * without a "this view is redacted" affordance implies the record had no SQL. The
 * two reasons get different copy because they have different remedies.
 */
export function projectionNotice(p: AuditProjection | undefined): TemplateResult | typeof nothing {
  if (!p?.applied) return nothing;
  const omitted = (p.omitted ?? []).join(", ");
  return html`<bn-panel eyebrow="This view is redacted">
    <p>
      ${p.reason === "credential_class_scoped"
        ? html`You are reading with a <strong>fine-grained token</strong>. A
            <code>sluice_ft_</code> token never receives the sensitive fields at any role, so this
            cannot be granted — read with a session or a general key instead. The aggregates and the
            index stay fully reachable: a CI token has a legitimate reason to count denials and none
            to read another person's SQL.`
        : html`Your organization role is <strong>below maintainer</strong>. The sensitive fields are
            withheld from viewers and members; an organization owner can raise your role.`}
    </p>
    <p class="meta">
      Withheld from this response: <code>${omitted || "sensitive fields"}</code>. They are absent
      from the response object rather than blanked, so nothing here implies a record had no SQL.
    </p>
  </bn-panel>`;
}

/** SOURCE_META describes which tier answered an analytics response. `daily` is
 * the one that must be loud: it is coarser in four separate ways and rendering it
 * as if it were fine-grained is the failure the field exists to prevent. */
export const SOURCE_META: Record<
  AnalyticsSource,
  { label: string; variant: BnBadgeVariant; note: string }
> = {
  detail: {
    label: "detail tier",
    variant: "success",
    note: "One row per audit record. Exact.",
  },
  rollup: {
    label: "rollup tier",
    variant: "info",
    note:
      "Pre-aggregated hourly rollups, used for ranges of a week or more. Percentiles are " +
      "re-derived from 13 histogram buckets by interpolation — exact enough for a trend, not " +
      "exact enough to quote to three significant figures.",
  },
  daily: {
    label: "daily rollup — worker unavailable",
    variant: "warning",
    note:
      "The analytics worker could not be reached, so this came from the per-day rollup in the " +
      "control-plane database. The grain is forced to one day, grouping is limited to the " +
      "decision, top-N lists were already capped when they were stored, and duration_p99 is the " +
      "stored MAXIMUM rather than a percentile.",
  },
  empty: {
    label: "nothing compacted yet",
    variant: "neutral",
    note:
      "Nothing has been compacted for this scope, so the shape is zero-filled rather than an " +
      "error. Read it with the ingest watermark: this is “not yet ingested”, not “no traffic”.",
  },
};

/** sourceBadge names the tier that answered. */
export function sourceBadge(source: AnalyticsSource | undefined): TemplateResult | typeof nothing {
  if (!source) return nothing;
  const meta = SOURCE_META[source];
  if (!meta) return nothing;
  return html`<bn-badge .variant=${meta.variant} title=${meta.note}>${meta.label}</bn-badge>`;
}

/* ── errors ─────────────────────────────────────────────────────────────── */

/**
 * errorCard renders a failed load with the `apierr` code, the server's message
 * and the `request_id` a reader quotes in a support ticket (`SC-S907`).
 *
 * A code this bundle does not know still renders: `06` §1.3 makes a new error code
 * additive rather than breaking, so the server's own message is shown rather than
 * a guess.
 */
export function errorCard(
  heading: string,
  err: ApiError,
  retry?: () => void,
  extra?: TemplateResult | typeof nothing,
): TemplateResult {
  return html`<bn-card heading=${heading}>
    <div role="alert">
      <p class="error-message">${err.message}</p>
      <bn-kv
        .rows=${[
          { label: "Error code", value: err.code },
          ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
        ]}
      ></bn-kv>
      ${err.requestId
        ? nothing
        : html`<p class="meta">
            The request did not reach Sluice Cloud, so there is no request id to quote. Check your
            connection and try again.
          </p>`}
      ${extra ?? nothing}
      ${retry
        ? html`<div class="row row-actions">
            <bn-button variant="primary" @click=${retry}>Try again</bn-button>
          </div>`
        : nothing}
    </div>
  </bn-card>`;
}

/* ── the error-code catalogue, read from the form schema ────────────────── */

/** SchemaEnumValue is one member of a closed enum in the form schema. */
interface SchemaEnumValue {
  value: string;
  label: string;
  help?: string;
}

let errorCodeCache: Promise<BnSelectOption[]> | null = null;

/**
 * errorCodeOptions returns the 22-code catalogue as select options, **read from
 * the form schema** rather than restated here.
 *
 * The schema is frozen interface I3 and is drift-checked in CI against the pinned
 * Sluice source (`SC-F501`), so a code added upstream fails the build instead of
 * silently narrowing this filter. A hard-coded list in a TypeScript file has
 * nothing defending it, which is the whole reason the schema is the contract.
 *
 * A failure to fetch it degrades to an empty list and the caller falls back to a
 * free-text field — losing the help lines, not the filter.
 */
export function errorCodeOptions(): Promise<BnSelectOption[]> {
  errorCodeCache ??= getPolicyFormSchema()
    .then((res) => {
      const enums = (res.schema?.enums ?? {}) as Record<string, { values?: SchemaEnumValue[] }>;
      const values = enums.ErrorCode?.values ?? [];
      return values.map((v) => ({
        value: v.value,
        label: v.label || v.value,
        description: v.help,
      }));
    })
    .catch(() => {
      // Drop the cache on failure. Caching the empty answer would make one
      // transient 503 cost every filter on every screen for the rest of the
      // session, which is a worse outcome than one extra request.
      errorCodeCache = null;
      return [] as BnSelectOption[];
    });
  return errorCodeCache;
}

/**
 * errorCodeHelp looks a code's human meaning out of the same schema, for the
 * record drawer's decision section (`04` §7.2 item 1: "`error_code` with its
 * human message from the 22-code catalogue").
 *
 * Most of the 22 carry no help line in the schema — the code *is* the sentence for
 * `ERR_TIMEOUT` — so an empty answer is normal and the drawer shows the code
 * alone rather than inventing prose for it.
 */
export async function errorCodeHelp(code: string): Promise<string> {
  const opts = await errorCodeOptions();
  return opts.find((o) => o.value === code)?.description ?? "";
}

/* ── policy references ──────────────────────────────────────────────────── */

/** PolicyRef is one `kind/name` string off a record's `applied_policies`. */
export interface PolicyRef {
  kind: string;
  name: string;
  raw: string;
}

/**
 * parsePolicyRef splits `Kind/name` as the record spells it.
 *
 * A policy name can itself contain a slash, so only the **first** separator is
 * significant. A value with no separator at all is kept whole as the name with an
 * empty kind, and the caller renders it as unlinked text: the editor route is
 * addressed by `(kind, name)` and a link missing half its key would 404.
 */
export function parsePolicyRef(raw: string): PolicyRef {
  const i = raw.indexOf("/");
  if (i <= 0) return { kind: "", name: raw, raw };
  return { kind: raw.slice(0, i), name: raw.slice(i + 1), raw };
}

/** policyHref is the editor route for one policy (`SC-F809`'s drill-through).
 * Empty when the reference cannot be resolved to a `(kind, name)` pair. */
export function policyHref(instanceId: string, ref: PolicyRef): string {
  if (!ref.kind || !ref.name) return "";
  return hrefFor("instances", instanceId, "policies", ref.kind, ref.name);
}

/* ── time ranges ────────────────────────────────────────────────────────── */

/** RangePreset is one entry of the range picker. `04` §6.1 fixes the four
 * presets; `custom` is the escape hatch. */
export interface RangePreset {
  id: string;
  label: string;
  hours: number;
}

/** RANGE_PRESETS is `04` §6.1's set. The default is 24 h, which is also the
 * server's default when a caller names no range. */
export const RANGE_PRESETS: readonly RangePreset[] = [
  { id: "24h", label: "Last 24 hours", hours: 24 },
  { id: "7d", label: "Last 7 days", hours: 24 * 7 },
  { id: "30d", label: "Last 30 days", hours: 24 * 30 },
  { id: "90d", label: "Last 90 days", hours: 24 * 90 },
];

/** rangeFor turns a preset id into an explicit half-open `[from, to)` in UTC.
 * The bounds are materialised rather than left implicit so the URL is shareable
 * and two charts on one screen cannot disagree about "now". */
export function rangeFor(hours: number, now = Date.now()): { from: string; to: string } {
  return {
    to: new Date(now).toISOString(),
    from: new Date(now - hours * 3600_000).toISOString(),
  };
}

/** hoursBetween is the window length, used to decide whether a `q=` text search
 * is inside the server's 7-day cap before the request is sent. */
export function hoursBetween(from: string, to: string): number {
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return (b - a) / 3600_000;
}

/* ── shared styles ──────────────────────────────────────────────────────── */

/**
 * auditSharedCss is the layout and notice vocabulary the three pages share.
 *
 * Every value is a `--bino-*` token. The chart series slots are declared here as
 * **aliases** of the semantic tokens rather than as colours, which is what makes
 * them follow the theme without a single `prefers-color-scheme` query in this
 * directory: `--series-*` resolves through `--bino-*`, and `--bino-*` is already
 * light/dark aware.
 */
export const auditSharedCss = css`
  :host {
    display: block;
    font-family: var(--bino-font-sans);
    color: var(--bino-text);

    /* Chart series slots. Aliases, never literals — see the doc comment.
       series-neutral is the one-hue magnitude slot every single-series chart and
       every inline table bar uses; series-deny and series-warn are status tones
       and are used only where the series genuinely MEANS a refusal or a fault,
       never as "series 4". Validated with the dataviz palette checker in both
       modes: worst all-pairs CVD delta-E 12.5 light / 13.9 dark, normal-vision
       24.0 / 22.1. */
    --series-neutral: var(--bino-primary);
    --series-warn: var(--bino-warning);
    --series-deny: var(--bino-decision-deny);
    --series-allow: var(--bino-decision-allow);
    --series-track: var(--bino-surface-inset);
  }

  h1 {
    margin: 0;
    font-size: var(--bino-font-size-xl);
    font-weight: 600;
  }
  h2 {
    margin: 0 0 var(--bino-space-sm);
    font-size: var(--bino-font-size-md);
    font-weight: 600;
  }
  /* h4 is the sub-heading inside a record section — a label over a bn-kv block,
     not a document heading. It wears the meta type so the section's own h3 keeps
     the visual hierarchy while the outline stays correct for a screen reader. */
  h4 {
    margin: var(--bino-space-md) 0 var(--bino-space-xs);
    font-size: var(--bino-font-size-sm);
    font-weight: 600;
    color: var(--bino-text-secondary);
  }
  header {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: var(--bino-space-md);
    margin-bottom: var(--bino-space-md);
  }
  .head-text {
    flex: 1 1 24rem;
    min-width: 0;
  }
  .head-actions {
    display: flex;
    align-items: center;
    gap: var(--bino-space-sm);
    flex-wrap: wrap;
  }
  .lede {
    margin: var(--bino-space-xs) 0 0;
    font-size: var(--bino-font-size-md);
    color: var(--bino-text-secondary);
    max-width: 70ch;
  }
  .meta {
    font-size: var(--bino-font-size-sm);
    color: var(--bino-text-secondary);
  }
  p.meta {
    margin: var(--bino-space-xs) 0 0;
    max-width: 80ch;
  }
  .row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--bino-space-sm);
  }
  .row-actions {
    margin-top: var(--bino-space-md);
  }
  .stack {
    display: grid;
    gap: var(--bino-space-md);
  }
  .frame {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--bino-space-sm);
    margin-bottom: var(--bino-space-sm);
  }

  /* Notices. notice-warn is for a fact the reader must not miss; notice-quiet is
     for one they must be able to find. Neither is decorative — both carry an
     honesty obligation from architecture doc 04. */
  .notice {
    margin: 0 0 var(--bino-space-md);
    padding: var(--bino-space-sm) var(--bino-space-md);
    border: 1px solid var(--bino-border);
    border-radius: var(--bino-radius);
    background: var(--bino-surface-subtle);
    font-size: var(--bino-font-size-md);
    color: var(--bino-text-muted);
    max-width: 100ch;
  }
  .notice-warn {
    background: var(--bino-warning-bg);
    border-color: var(--bino-warning-border);
    color: var(--bino-warning-text);
  }
  .notice-error {
    background: var(--bino-error-bg);
    border-color: var(--bino-error-border);
    color: var(--bino-text);
  }
  .notice code,
  code {
    font-family: var(--bino-font-mono);
    font-size: var(--bino-font-size-base);
  }

  .error-message {
    margin: 0 0 var(--bino-space-md);
    font-size: var(--bino-font-size-md);
    color: var(--bino-text);
    max-width: 70ch;
  }

  /* Chips — tables, catalogs and applied policies in the record drawer. A chip
     that navigates is an <a>; a chip that filters is a <button>. Never a <div>
     with a click handler: both need to be reachable from the keyboard. */
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--bino-space-xs);
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: var(--bino-space-xs);
    padding: 0.0625rem var(--bino-space-sm);
    border: 1px solid var(--bino-border-light);
    border-radius: var(--bino-radius-pill);
    background: var(--bino-surface);
    color: var(--bino-text);
    font-family: var(--bino-font-mono);
    font-size: var(--bino-font-size-sm);
    text-decoration: none;
    cursor: pointer;
  }
  .chip:hover {
    background: var(--bino-surface-hover);
    border-color: var(--bino-border-hover);
  }
  .chip:focus-visible {
    outline: none;
    box-shadow: var(--bino-focus-ring-shadow);
  }
  .chip[aria-disabled="true"] {
    cursor: default;
    color: var(--bino-text-secondary);
    background: var(--bino-surface-subtle);
  }

  .sub {
    display: block;
    font-size: var(--bino-font-size-sm);
    color: var(--bino-text-secondary);
  }
  .mono {
    font-family: var(--bino-font-mono);
    font-size: var(--bino-font-size-base);
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    clip-path: inset(50%);
    border: 0;
  }

  /* Section headings inside the drawer / record page. */
  .section {
    margin-top: var(--bino-space-lg);
  }
  .section:first-of-type {
    margin-top: 0;
  }
  .section > h3 {
    margin: 0 0 var(--bino-space-sm);
    font-size: var(--bino-font-size-sm);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--bino-primary);
  }

  /* Filter toolbar. One row above everything it scopes; never per-card. */
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: var(--bino-space-md);
    margin-bottom: var(--bino-space-md);
  }
  .toolbar bn-field {
    margin-bottom: 0;
  }
  .field-sm {
    width: 10rem;
  }
  .field-md {
    width: 14rem;
  }
  .field-grow {
    flex: 1 1 16rem;
    min-width: 12rem;
  }
  .toolbar-end {
    display: flex;
    align-items: center;
    gap: var(--bino-space-sm);
    margin-left: auto;
  }
`;
